"use client";

import { useEffect, useState } from "react";
import type { UserTagSuggestion } from "@/components/posts/user-tag-picker";
import { Label } from "@/components/ui/label";
import {
  ModeToggle,
  type Participant,
  suggestionToParticipant,
  TeamCard,
  ValidationWarning,
} from "./base-match-template";
import { ActivityCourtPreview, ActivityDisplayPreview } from "./previews";
import { BadmintonScoreEditor } from "./score-editors/badminton-score-editor";

// ──────────────────────────────────────────────────────────────────────────────
// TYPES
// ──────────────────────────────────────────────────────────────────────────────

export type BadmintonMode = "singles" | "doubles";

export type BadmintonGame = {
  yourScore: number;
  opponentScore: number;
};

export type BadmintonTemplateData = {
  mode: BadmintonMode;
  matchScore: { yourGames: number; opponentGames: number };
  games: BadmintonGame[];
  positions: Record<string, "your-team" | "opponent-team">;
  hasScore?: boolean;
};

export type BadmintonParticipant = Participant;

// ──────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ──────────────────────────────────────────────────────────────────────────────

type BadmintonTemplateProps = {
  currentUser: BadmintonParticipant;
  onChange: (
    data: BadmintonTemplateData,
    participants: BadmintonParticipant[],
  ) => void;
  initialData?: Partial<BadmintonTemplateData>;
};

export function BadmintonTemplate({
  currentUser,
  onChange,
  initialData,
}: BadmintonTemplateProps) {
  const [mode, setMode] = useState<BadmintonMode>(
    initialData?.mode ?? "singles",
  );
  const [participants, setParticipants] = useState<BadmintonParticipant[]>([
    currentUser,
  ]);
  const [teams, setTeams] = useState<
    Record<string, "your-team" | "opponent-team">
  >(() => ({
    [currentUser.id]: "your-team",
    ...(initialData?.positions ?? {}),
  }));
  type BadmintonScorePart = Pick<BadmintonTemplateData, "matchScore" | "games">;
  const [scorePart, setScorePart] = useState<BadmintonScorePart>(() => ({
    matchScore: initialData?.matchScore ?? { yourGames: 0, opponentGames: 0 },
    games: initialData?.games ?? [],
  }));

  const maxParticipants = mode === "singles" ? 2 : 4;
  const yourTeamMax = mode === "singles" ? 1 : 2;
  const opponentTeamMax = mode === "singles" ? 1 : 2;

  const yourTeam = participants.filter((p) => teams[p.id] === "your-team");
  const opponentTeam = participants.filter(
    (p) => teams[p.id] === "opponent-team",
  );
  const selectedUserIds = new Set(participants.map((p) => p.id));

  useEffect(() => {
    const data: BadmintonTemplateData = {
      mode,
      matchScore: scorePart.matchScore,
      games: scorePart.games ?? [],
      positions: teams,
      hasScore: true,
    };
    onChange(data, participants);
  }, [mode, scorePart, teams, participants, onChange]);

  const handleModeChange = (newMode: BadmintonMode) => {
    setMode(newMode);
    if (newMode === "singles") {
      const yourTeamFiltered = yourTeam.slice(0, 1);
      const opponentFiltered = opponentTeam.slice(0, 1);
      const newParticipants = [...yourTeamFiltered, ...opponentFiltered];
      if (!newParticipants.find((p) => p.id === currentUser.id)) {
        newParticipants.unshift(currentUser);
      }
      setParticipants(newParticipants);
      const newTeams: Record<string, "your-team" | "opponent-team"> = {
        [currentUser.id]: "your-team",
      };
      for (const p of opponentFiltered) {
        newTeams[p.id] = "opponent-team";
      }
      setTeams(newTeams);
    }
  };

  const yourGames = scorePart.matchScore.yourGames;
  const opponentGames = scorePart.matchScore.opponentGames;

  const handleAddParticipant = (
    suggestion: UserTagSuggestion,
    team: "your-team" | "opponent-team",
  ) => {
    if (participants.length >= maxParticipants) return;
    const teamCount =
      team === "your-team" ? yourTeam.length : opponentTeam.length;
    const teamMax = team === "your-team" ? yourTeamMax : opponentTeamMax;
    if (teamCount >= teamMax) return;

    const newParticipant = suggestionToParticipant(suggestion);
    setParticipants((prev) => [...prev, newParticipant]);
    setTeams((prev) => ({ ...prev, [suggestion.id]: team }));
  };

  const handleRemoveParticipant = (userId: string) => {
    if (userId === currentUser.id) return;
    setParticipants((prev) => prev.filter((p) => p.id !== userId));
    setTeams((prev) => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
  };

  const yourWon = yourGames > opponentGames;
  const opponentWon = opponentGames > yourGames;
  const isDraw = yourGames === opponentGames && yourGames > 0;
  const isYourTeamComplete = yourTeam.length === yourTeamMax;
  const isOpponentComplete = opponentTeam.length === opponentTeamMax;
  const isComplete = isYourTeamComplete && isOpponentComplete;

  return (
    <div className="space-y-6">
      {/* Mode Selection */}
      <ModeToggle
        label="Match Type"
        options={[
          { value: "singles", label: "Singles" },
          { value: "doubles", label: "Doubles" },
        ]}
        value={mode}
        onChange={handleModeChange}
      />

      {/* Teams */}
      <div className="grid grid-cols-2 gap-3">
        <TeamCard
          label="Team 1"
          isYourTeam
          players={yourTeam}
          maxPlayers={yourTeamMax}
          currentUserId={currentUser.id}
          selectedUserIds={selectedUserIds}
          onAddPlayer={(s) => handleAddParticipant(s, "your-team")}
          onRemovePlayer={handleRemoveParticipant}
          highlight={yourWon || isDraw}
          isComplete={isYourTeamComplete}
        />
        <TeamCard
          label="Team 2"
          isYourTeam={false}
          players={opponentTeam}
          maxPlayers={opponentTeamMax}
          currentUserId={currentUser.id}
          selectedUserIds={selectedUserIds}
          onAddPlayer={(s) => handleAddParticipant(s, "opponent-team")}
          onRemovePlayer={handleRemoveParticipant}
          highlight={opponentWon}
          isComplete={isOpponentComplete}
        />
      </div>

      {!isComplete && (
        <ValidationWarning
          message={
            !isYourTeamComplete && !isOpponentComplete
              ? "Add players to both sides to post"
              : !isOpponentComplete
                ? `Add ${opponentTeamMax - opponentTeam.length} more opponent${opponentTeamMax - opponentTeam.length > 1 ? "s" : ""}`
                : ""
          }
        />
      )}

      <BadmintonScoreEditor
        initialData={initialData as unknown as Record<string, unknown>}
        onChange={(data) => setScorePart(data as unknown as BadmintonScorePart)}
      />

      {/* Court Preview */}
      {participants.length >= 2 && (
        <div className="space-y-2">
          <Label className="text-text-primary">Preview</Label>
          <ActivityCourtPreview
            activityId="badminton"
            mode={mode}
            yourTeam={yourTeam}
            opponentTeam={opponentTeam}
            yourWon={yourWon}
          />
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// DISPLAY COMPONENT (for feed)
// ──────────────────────────────────────────────────────────────────────────────

type BadmintonDisplayProps = {
  data: BadmintonTemplateData | null;
  participants: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }[];
};

export function BadmintonDisplay({
  data,
  participants,
}: BadmintonDisplayProps) {
  return (
    <ActivityDisplayPreview
      activityId="badminton"
      data={data}
      participants={participants}
    />
  );
}
